/**
 * Setup wizard — onboarding for new merchants. Multichain V1.2.
 *
 * Steps:
 *   1. Name     — display name
 *   2. Keys     — generate-OR-import a Shieldz mnemonic; show words ONCE;
 *                  derive BTC zpub, EVM address, Zcash UFVK in-browser;
 *                  optional encrypted-keystore download
 *   3. Welcome  — ✓ Ready + tips + Take me in
 *
 * The mnemonic NEVER leaves the browser. We POST only the derived public
 * primitives. The merchant is responsible for backing up the words.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  ApiError,
  setAnyCryptoEnabled,
  setSwapRefundAddresses,
  upsertMyMerchant,
  verifySettlementSetup,
  type ChainConfigInput,
} from "../lib/api";
import {
  deriveAll,
  deriveAllRefunds,
  downloadKeystore,
  exportKeystore,
  generateShieldzMnemonic,
  isValidMnemonic,
  type DerivedPrimitives,
  type Keystore,
} from "../lib/key-gen";
import { Banner, Btn } from "../components/ui";
import { Ic } from "../components/Icons";
import { useT } from "../lib/i18n";

type Step = "name" | "method" | "address" | "keys" | "welcome";
type KeysMode = "generate" | "import";

// The five EVM chains we activate from the single derived EVM address.
const EVM_CHAINS = ["BASE", "ARB", "OP", "POLY", "ETH"] as const;

const EVM_ADDR_RE = /^0x[0-9a-fA-F]{40}$/;
const CHAIN_LABELS: Record<(typeof EVM_CHAINS)[number], string> = {
  BASE: "Base",
  ARB: "Arbitrum",
  OP: "Optimism",
  POLY: "Polygon",
  ETH: "Ethereum",
};

const trunc = (s: string, head = 10, tail = 6): string =>
  s.length <= head + tail + 1 ? s : `${s.slice(0, head)}…${s.slice(-tail)}`;

export function SetupScreen() {
  const t = useT();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>("name");
  const [displayName, setDisplayName] = useState("");
  // Which onboarding path the merchant took — drives the welcome copy.
  const [mode, setMode] = useState<"address" | "keys" | null>(null);

  // Quick-start (paste-an-address) state.
  const [addr, setAddr] = useState("");
  const [addrChain, setAddrChain] = useState<(typeof EVM_CHAINS)[number]>("BASE");
  const [addrConfirmed, setAddrConfirmed] = useState(false);

  // Keys step state
  const [keysMode, setKeysMode] = useState<KeysMode>("generate");
  const [mnemonic, setMnemonic] = useState<string>("");
  const [importWords, setImportWords] = useState<string>("");
  const [revealed, setRevealed] = useState(false);
  const [savedConfirmed, setSavedConfirmed] = useState(false);
  const [deriving, setDeriving] = useState(false);
  const [derived, setDerived] = useState<DerivedPrimitives | null>(null);
  const [deriveError, setDeriveError] = useState<string | null>(null);

  // Encrypted keystore export
  const [showKeystoreModal, setShowKeystoreModal] = useState(false);
  const [keystorePass, setKeystorePass] = useState("");
  const [keystorePass2, setKeystorePass2] = useState("");
  const [keystoreBusy, setKeystoreBusy] = useState(false);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset derived primitives if mnemonic source changes
  useEffect(() => {
    setDerived(null);
    setDeriveError(null);
  }, [mnemonic]);

  const mnemonicWords = useMemo(
    () => mnemonic.trim().split(/\s+/).filter(Boolean),
    [mnemonic],
  );

  const handleNameNext = () => {
    if (displayName.trim().length === 0) {
      setError(t("setup.err_name_required"));
      return;
    }
    setError(null);
    setStep("method");
  };

  const chooseKeys = () => {
    setMode("keys");
    setError(null);
    setStep("keys");
    if (!mnemonic && keysMode === "generate") {
      setMnemonic(generateShieldzMnemonic());
    }
  };

  const chooseAddress = () => {
    setMode("address");
    setError(null);
    setStep("address");
  };

  // Quick-start submit: a single pasted EVM address as the settlement target.
  // No seed phrase — funds settle straight to an address the merchant already
  // controls. They can add shielded ZEC/BTC later (Coins → connect keystore).
  const handleAddressSubmit = async () => {
    const a = addr.trim();
    if (!EVM_ADDR_RE.test(a)) {
      setError(t("setup.err_invalid_address"));
      return;
    }
    if (!addrConfirmed) {
      setError(t("setup.err_confirm_control"));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const result = await upsertMyMerchant({
        display_name: displayName.trim(),
        logo_url: null,
        chains: [{ chain_id: addrChain, primitive_kind: "address", primitive_value: a }],
      });
      if (result.chains) {
        const failures = result.chains.filter((c) => c.status === "error");
        if (failures.length > 0) {
          setError(
            t("setup.err_server_rejected_address") + " " +
              failures.map((f) => `${f.chain_id} (${f.reason ?? "unknown"})`).join(", "),
          );
          return;
        }
      }
      // Quick-start = settle EVERYTHING to the one chosen token. One-token mode
      // means only that token settles natively; any other crypto routes through
      // the swap path to it. Best-effort — they can also toggle it on Coins.
      try {
        await setAnyCryptoEnabled(true);
      } catch {
        /* non-fatal — the merchant exists; toggle later on the Coins page */
      }
      await verifySettlementSetup();
      setStep("welcome");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const handleRegenerate = () => {
    setRevealed(false);
    setSavedConfirmed(false);
    setDerived(null);
    setMnemonic(generateShieldzMnemonic());
  };

  const handleAdoptImport = () => {
    const trimmed = importWords.trim().toLowerCase().split(/\s+/).join(" ");
    if (!isValidMnemonic(trimmed)) {
      setError(t("setup.err_invalid_mnemonic"));
      return;
    }
    setError(null);
    setMnemonic(trimmed);
    setRevealed(true);
    setSavedConfirmed(true); // they typed it in — they have it
  };

  const handleDerive = useCallback(async () => {
    if (!mnemonic) return;
    setDeriving(true);
    setDeriveError(null);
    try {
      const out = await deriveAll(mnemonic);
      setDerived(out);
    } catch (e) {
      setDeriveError(e instanceof Error ? e.message : String(e));
    } finally {
      setDeriving(false);
    }
  }, [mnemonic]);

  // Auto-kick derivation as soon as we have a mnemonic AND the merchant
  // confirmed they saved it (or imported their own).
  useEffect(() => {
    if (mnemonic && savedConfirmed && !derived && !deriving && !deriveError) {
      void handleDerive();
    }
  }, [mnemonic, savedConfirmed, derived, deriving, deriveError, handleDerive]);

  const handleSubmit = async () => {
    if (!derived) {
      setError(t("setup.err_still_deriving"));
      return;
    }
    const chains: ChainConfigInput[] = [
      { chain_id: "ZEC", primitive_kind: "ufvk", primitive_value: derived.zec_ufvk },
      { chain_id: "BTC", primitive_kind: "xpub", primitive_value: derived.btc_zpub },
      ...EVM_CHAINS.map(
        (id): ChainConfigInput => ({
          chain_id: id,
          primitive_kind: "address",
          primitive_value: derived.evm_address,
        }),
      ),
    ];
    setError(null);
    setBusy(true);
    try {
      const result = await upsertMyMerchant({
        display_name: displayName.trim(),
        logo_url: null,
        chains,
      });
      if (result.chains) {
        const failures = result.chains.filter((c) => c.status === "error");
        if (failures.length > 0) {
          setError(
            t("setup.err_chains_failed_validation") + " " +
              failures
                .map((f) => `${f.chain_id} (${f.reason ?? "unknown"})`)
                .join(", "),
          );
          return;
        }
      }

      // We already hold the seed, so derive the swap-in refund addresses for
      // every coin we CAN derive (Tron/Solana/NEAR/LTC/Dash/BCH/ZEC-transparent)
      // and save them now — no reason to make the merchant re-connect their
      // keystore on the Coins page to light these up. TON is contract-derived
      // (needs the TON SDK), so it stays custom-address-only. Best-effort:
      // a failure here must not block finishing setup — the Coins page's
      // "connect keystore" flow remains the fallback.
      try {
        const r = deriveAllRefunds(mnemonic);
        await setSwapRefundAddresses({
          TRON: r.tron,
          SOL: r.solana,
          NEAR: r.near,
          LTC: r.ltc,
          DASH: r.dash,
          BCH: r.bch,
          ZEC: r.zec,
        });
      } catch {
        /* non-fatal — merchant can derive these later on the Coins page */
      }

      await verifySettlementSetup();
      setStep("welcome");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const handleExportKeystore = async () => {
    if (!mnemonic) return;
    if (keystorePass !== keystorePass2) {
      setError(t("setup.err_passphrases_mismatch"));
      return;
    }
    if (keystorePass.length < 8) {
      setError(t("setup.err_passphrase_too_short"));
      return;
    }
    setError(null);
    setKeystoreBusy(true);
    try {
      const ks: Keystore = await exportKeystore(mnemonic, keystorePass);
      const today = new Date().toISOString().slice(0, 10);
      downloadKeystore(ks, `shieldz-keystore-${today}.json`);
      setShowKeystoreModal(false);
      setKeystorePass("");
      setKeystorePass2("");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setKeystoreBusy(false);
    }
  };

  return (
    <>
      {error && (
        <div style={{ marginBottom: 12 }}>
          <Banner tone="fail">{error}</Banner>
        </div>
      )}

      {step === "name" && (
        <div className="fade-in">
          <h1 style={{ fontSize: 22, letterSpacing: "-0.02em", margin: "4px 0 6px" }}>
            {t("setup.name_heading")}
          </h1>
          <p style={{ color: "var(--ink-2)", margin: "0 0 18px", fontSize: 14, lineHeight: 1.55 }}>
            {t("setup.name_body")}
          </p>
          <div className="field">
            <label htmlFor="dn">{t("setup.display_name_label")}</label>
            <input
              id="dn"
              className="input"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={t("setup.display_name_placeholder")}
              maxLength={120}
              autoFocus
            />
          </div>
          <Btn block onClick={handleNameNext} style={{ marginTop: 20 }}>
            {t("setup.continue")}
          </Btn>
        </div>
      )}

      {step === "method" && (
        <div className="fade-in">
          <h1 style={{ fontSize: 22, letterSpacing: "-0.02em", margin: "4px 0 6px" }}>
            {t("setup.method_heading")}
          </h1>
          <p style={{ color: "var(--ink-2)", margin: "0 0 18px", fontSize: 14, lineHeight: 1.55 }}>
            {t("setup.method_body")}
          </p>

          <MethodCard
            title={t("setup.method_quickstart_title")}
            badge={t("setup.method_recommended_badge")}
            body={t("setup.method_quickstart_body")}
            onClick={chooseAddress}
          />
          <div style={{ height: 10 }} />
          <MethodCard
            title={t("setup.method_selfcustody_title")}
            body={t("setup.method_selfcustody_body")}
            onClick={chooseKeys}
          />

          <button
            type="button"
            className="auth-link"
            style={{
              marginTop: 16,
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "var(--ink-3)",
              fontSize: 13,
            }}
            onClick={() => setStep("name")}
          >
            ← {t("setup.back")}
          </button>
        </div>
      )}

      {step === "address" && (
        <div className="fade-in">
          <h1 style={{ fontSize: 22, letterSpacing: "-0.02em", margin: "4px 0 6px" }}>
            {t("setup.address_heading")}
          </h1>
          <p style={{ color: "var(--ink-2)", margin: "0 0 16px", fontSize: 14, lineHeight: 1.55 }}>
            {t("setup.address_body_before")}{" "}
            <strong>{t("setup.address_body_one_token")} {CHAIN_LABELS[addrChain]} USDC</strong>{" "}
            {t("setup.address_body_after")}
          </p>

          <div className="field">
            <label htmlFor="achain">{t("setup.network_label")}</label>
            <select
              id="achain"
              className="input"
              value={addrChain}
              onChange={(e) => setAddrChain(e.target.value as (typeof EVM_CHAINS)[number])}
            >
              {EVM_CHAINS.map((c) => (
                <option key={c} value={c}>
                  {CHAIN_LABELS[c]} · USDC
                </option>
              ))}
            </select>
          </div>

          <div className="field" style={{ marginTop: 12 }}>
            <label htmlFor="aaddr">
              {t("setup.address_field_label_before")} {CHAIN_LABELS[addrChain]} USDC{" "}
              {t("setup.address_field_label_after")}
            </label>
            <input
              id="aaddr"
              className="input mono"
              type="text"
              value={addr}
              onChange={(e) => setAddr(e.target.value)}
              placeholder="0x…"
              autoComplete="off"
              spellCheck={false}
              style={{ fontSize: 13 }}
            />
          </div>

          <div
            className={`check-confirm${addrConfirmed ? " on" : ""}`}
            onClick={() => setAddrConfirmed((c) => !c)}
            role="checkbox"
            aria-checked={addrConfirmed}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === " " || e.key === "Enter") {
                e.preventDefault();
                setAddrConfirmed((c) => !c);
              }
            }}
            style={{ cursor: "pointer", marginTop: 14 }}
          >
            <div className="cb">{addrConfirmed && <Ic name="check" size={14} />}</div>
            <div className="ct">
              {t("setup.address_confirm_before")}{" "}
              <strong>{CHAIN_LABELS[addrChain]}</strong>. {t("setup.address_confirm_after")}
            </div>
          </div>

          <Btn
            block
            onClick={() => void handleAddressSubmit()}
            disabled={busy}
            loading={busy}
            style={{ marginTop: 20 }}
          >
            {t("setup.start_accepting_payments")}
          </Btn>
          <button
            type="button"
            className="auth-link"
            style={{
              marginTop: 12,
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "var(--ink-3)",
              fontSize: 13,
            }}
            onClick={() => setStep("method")}
          >
            ← {t("setup.back")}
          </button>
        </div>
      )}

      {step === "keys" && (
        <div className="fade-in">
          <h1 style={{ fontSize: 22, letterSpacing: "-0.02em", margin: "4px 0 6px" }}>
            {t("setup.keys_heading")}
          </h1>
          <p style={{ color: "var(--ink-2)", margin: "0 0 14px", fontSize: 14, lineHeight: 1.55 }}>
            {t("setup.keys_body_before")} <strong>{t("setup.keys_body_never_sees")}</strong> {t("setup.keys_body_after")}
          </p>

          <div
            style={{
              display: "flex",
              gap: 6,
              marginBottom: 14,
              padding: 4,
              background: "var(--surface-2)",
              borderRadius: 10,
            }}
          >
            <ModeTab
              active={keysMode === "generate"}
              onClick={() => {
                setKeysMode("generate");
                if (!mnemonic) setMnemonic(generateShieldzMnemonic());
              }}
            >
              {t("setup.keys_tab_generate")}
            </ModeTab>
            <ModeTab
              active={keysMode === "import"}
              onClick={() => {
                setKeysMode("import");
                setMnemonic("");
                setRevealed(false);
                setSavedConfirmed(false);
              }}
            >
              {t("setup.keys_tab_import")}
            </ModeTab>
          </div>

          {keysMode === "generate" && (
            <>
              <div className="s-label" style={{ marginTop: 0 }}>
                {t("setup.keys_recovery_phrase_label")}
              </div>
              <div
                className="card"
                style={{
                  padding: 14,
                  position: "relative",
                  marginBottom: 12,
                  minHeight: 110,
                }}
              >
                {!revealed ? (
                  <button
                    type="button"
                    onClick={() => setRevealed(true)}
                    style={{
                      position: "absolute",
                      inset: 0,
                      width: "100%",
                      height: "100%",
                      borderRadius: 12,
                      background:
                        "linear-gradient(135deg, var(--surface-2) 0%, var(--brand-50) 100%)",
                      border: 0,
                      cursor: "pointer",
                      fontWeight: 600,
                      color: "var(--ink)",
                      fontSize: 14,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                    }}
                  >
                    <Ic name="check" size={16} />
                    {t("setup.keys_tap_to_reveal")}
                  </button>
                ) : (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(3, 1fr)",
                      gap: 6,
                    }}
                  >
                    {mnemonicWords.map((w, i) => (
                      <div
                        key={`${i}-${w}`}
                        style={{
                          padding: "8px 10px",
                          borderRadius: 8,
                          background: "var(--surface-2)",
                          fontSize: 13,
                          fontFamily: "var(--font-mono, ui-monospace)",
                          display: "flex",
                          gap: 6,
                          alignItems: "baseline",
                        }}
                      >
                        <span style={{ color: "var(--ink-4)", fontSize: 10 }}>
                          {i + 1}
                        </span>
                        <span>{w}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {revealed && (
                <>
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      marginBottom: 12,
                    }}
                  >
                    <Btn
                      variant="ghost"
                      onClick={() => {
                        void navigator.clipboard.writeText(mnemonic);
                      }}
                      style={{ flex: 1 }}
                    >
                      <Ic name="copy" size={14} /> {t("setup.keys_copy")}
                    </Btn>
                    <Btn
                      variant="ghost"
                      onClick={() => setShowKeystoreModal(true)}
                      style={{ flex: 1 }}
                    >
                      <Ic name="download" size={14} /> {t("setup.keys_download_encrypted")}
                    </Btn>
                    <Btn
                      variant="ghost"
                      onClick={handleRegenerate}
                      style={{ flex: 1 }}
                    >
                      {t("setup.keys_regenerate")}
                    </Btn>
                  </div>

                  <div
                    className={`check-confirm${savedConfirmed ? " on" : ""}`}
                    onClick={() => setSavedConfirmed((c) => !c)}
                    role="checkbox"
                    aria-checked={savedConfirmed}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === " " || e.key === "Enter") {
                        e.preventDefault();
                        setSavedConfirmed((c) => !c);
                      }
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="cb">
                      {savedConfirmed && <Ic name="check" size={14} />}
                    </div>
                    <div className="ct">
                      {t("setup.keys_saved_confirm_before")}{" "}
                      <strong>{t("setup.keys_saved_confirm_strong")}</strong> {t("setup.keys_saved_confirm_after")}
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {keysMode === "import" && !mnemonic && (
            <>
              <div className="field" style={{ marginTop: 0 }}>
                <label htmlFor="impw">{t("setup.keys_import_label")}</label>
                <textarea
                  id="impw"
                  className="input mono"
                  value={importWords}
                  onChange={(e) => setImportWords(e.target.value)}
                  placeholder={t("setup.keys_import_placeholder")}
                  autoComplete="off"
                  spellCheck={false}
                  rows={3}
                  style={{ fontSize: 13 }}
                />
              </div>
              <p
                style={{
                  fontSize: 12,
                  color: "var(--ink-3)",
                  margin: "8px 0 12px",
                  lineHeight: 1.5,
                }}
              >
                {t("setup.keys_import_help")}
              </p>
              <Btn block onClick={handleAdoptImport}>
                {t("setup.keys_use_mnemonic")}
              </Btn>
            </>
          )}

          {mnemonic && savedConfirmed && (
            <>
              <div className="s-label">{t("setup.keys_derived_primitives")}</div>
              <div className="card card-pad">
                {!derived && deriving && (
                  <div style={{ fontSize: 13, color: "var(--ink-3)" }}>
                    {t("setup.keys_deriving")}
                  </div>
                )}
                {deriveError && (
                  <Banner tone="fail">{deriveError}</Banner>
                )}
                {derived && (
                  <div style={{ display: "grid", gap: 10 }}>
                    <DerivedRow
                      label="Zcash UFVK"
                      value={derived.zec_ufvk}
                    />
                    <DerivedRow
                      label="Bitcoin zpub"
                      value={derived.btc_zpub}
                    />
                    <DerivedRow
                      label="EVM address (Base · Arbitrum · OP · Polygon · Ethereum)"
                      value={derived.evm_address}
                    />
                  </div>
                )}
              </div>

              <Btn
                block
                onClick={() => void handleSubmit()}
                disabled={!derived || busy}
                loading={busy}
                style={{ marginTop: 20 }}
              >
                {t("setup.keys_activate")} {derived ? `7 ${t("setup.keys_chains")}` : "…"}
              </Btn>
            </>
          )}
        </div>
      )}

      {step === "welcome" && (
        <div className="fade-in" style={{ textAlign: "center" }}>
          <div className="result-check" style={{ marginTop: 6 }}>
            <Ic name="check" size={28} />
          </div>
          <h1 style={{ fontSize: 24, letterSpacing: "-0.02em", margin: "12px 0 4px" }}>
            {t("setup.welcome_ready_before")} {displayName}.
          </h1>
          <p
            style={{
              color: "var(--ink-2)",
              fontSize: 14,
              lineHeight: 1.55,
              margin: "0 auto 22px",
              maxWidth: "32ch",
            }}
          >
            {mode === "address"
              ? t("setup.welcome_body_address")
              : t("setup.welcome_body_keys")}
          </p>

          <div className="card card-pad" style={{ textAlign: "left", marginBottom: 18 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                marginBottom: 8,
                color: "var(--ink-2)",
              }}
            >
              {t("setup.welcome_tips_heading")}
            </div>
            <ul
              style={{
                margin: 0,
                paddingLeft: 16,
                color: "var(--ink-2)",
                fontSize: 13,
                lineHeight: 1.6,
              }}
            >
              <li>{t("setup.welcome_tip_fresh_address")}</li>
              <li>{t("setup.welcome_tip_straight_to_wallet")}</li>
              <li>{t("setup.welcome_tip_expiry")}</li>
              {mode === "address" ? (
                <li>
                  {t("setup.welcome_tip_add_keys_before")}{" "}
                  <strong>{t("setup.welcome_tip_add_keys_path")}</strong>.
                </li>
              ) : (
                <li>{t("setup.welcome_tip_backup")}</li>
              )}
            </ul>
          </div>

          <div className="card card-pad" style={{ textAlign: "left", marginBottom: 18 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 600,
                marginBottom: 10,
                color: "var(--ink-2)",
              }}
            >
              {t("setup.welcome_sell_heading")}
            </div>
            {(
              [
                {
                  icon: "lock",
                  title: t("setup.welcome_sell_file_title"),
                  sub: t("setup.welcome_sell_file_sub"),
                  to: "/unlocks",
                },
                {
                  icon: "share",
                  title: t("setup.welcome_sell_tips_title"),
                  sub: t("setup.welcome_sell_tips_sub"),
                  to: "/pages",
                },
                {
                  icon: "link",
                  title: t("setup.welcome_sell_invoice_title"),
                  sub: t("setup.welcome_sell_invoice_sub"),
                  to: "/",
                },
              ] as const
            ).map((r) => (
              <button
                key={r.to}
                type="button"
                onClick={() => void navigate({ to: r.to })}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  width: "100%",
                  textAlign: "left",
                  background: "none",
                  border: "none",
                  borderTop: "1px solid var(--line)",
                  padding: "11px 2px",
                  cursor: "pointer",
                  color: "var(--ink)",
                }}
              >
                <Ic name={r.icon} size={18} />
                <span style={{ flex: 1 }}>
                  <span style={{ display: "block", fontSize: 14, fontWeight: 600 }}>{r.title}</span>
                  <span style={{ display: "block", fontSize: 12.5, color: "var(--ink-2)" }}>{r.sub}</span>
                </span>
                <Ic name="chevron" size={16} />
              </button>
            ))}
          </div>

          {mode === "address" && (
            <Btn
              variant="ghost"
              block
              onClick={() => void navigate({ to: "/coins" })}
              style={{ marginBottom: 8 }}
            >
              {t("setup.welcome_add_private")} ZEC + BTC
            </Btn>
          )}
          <Btn block onClick={() => void navigate({ to: "/" })}>
            {t("setup.welcome_take_me_in")}
          </Btn>
        </div>
      )}

      {showKeystoreModal && (
        <KeystoreExportModal
          onClose={() => {
            setShowKeystoreModal(false);
            setKeystorePass("");
            setKeystorePass2("");
          }}
          pass={keystorePass}
          setPass={setKeystorePass}
          pass2={keystorePass2}
          setPass2={setKeystorePass2}
          busy={keystoreBusy}
          onExport={() => void handleExportKeystore()}
        />
      )}
    </>
  );
}

// ─── small components ───────────────────────────────────────────────

function MethodCard({
  title,
  badge,
  body,
  onClick,
}: {
  title: string;
  badge?: string;
  body: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="card card-pad"
      style={{
        display: "block",
        width: "100%",
        textAlign: "left",
        cursor: "pointer",
        border: "1px solid var(--line)",
        background: "var(--surface)",
        fontFamily: "inherit",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>{title}</span>
        {badge && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "var(--brand-600, #06845e)",
              background: "var(--brand-50, rgba(8,160,114,0.10))",
              borderRadius: 999,
              padding: "2px 8px",
            }}
          >
            {badge}
          </span>
        )}
        <Ic name="chevron" size={15} />
      </div>
      <div style={{ fontSize: 13, color: "var(--ink-2)", lineHeight: 1.5 }}>{body}</div>
    </button>
  );
}

function ModeTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: 1,
        padding: "9px 12px",
        borderRadius: 8,
        border: 0,
        background: active ? "var(--surface)" : "transparent",
        boxShadow: active ? "0 1px 3px rgba(0,0,0,.06)" : "none",
        color: active ? "var(--ink)" : "var(--ink-3)",
        fontSize: 13,
        fontWeight: 600,
        cursor: "pointer",
        fontFamily: "inherit",
      }}
    >
      {children}
    </button>
  );
}

function DerivedRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: "var(--ink-4)", marginBottom: 3 }}>
        {label}
      </div>
      <div
        style={{
          fontFamily: "var(--font-mono, ui-monospace)",
          fontSize: 11,
          color: "var(--ink-2)",
          wordBreak: "break-all",
          lineHeight: 1.5,
        }}
      >
        {trunc(value, 16, 10)}
      </div>
    </div>
  );
}

function KeystoreExportModal(props: {
  onClose: () => void;
  pass: string;
  setPass: (s: string) => void;
  pass2: string;
  setPass2: (s: string) => void;
  busy: boolean;
  onExport: () => void;
}) {
  const t = useT();
  return (
    <div
      className="overlay show"
      onClick={(e) => e.target === e.currentTarget && props.onClose()}
    >
      <div className="sheet">
        <div className="sheet-grab" />
        <div className="sheet-head">
          <h3>{t("setup.keystore_heading")}</h3>
          <button
            className="sheet-x"
            onClick={props.onClose}
            aria-label={t("setup.close")}
          >
            <Ic name="x" size={16} />
          </button>
        </div>
        <div className="sheet-body">
          <p style={{ fontSize: 13, color: "var(--ink-2)", lineHeight: 1.55 }}>
            {t("setup.keystore_body")}
          </p>
          <div className="field" style={{ marginTop: 14 }}>
            <label htmlFor="ksp1">{t("setup.keystore_passphrase_label")}</label>
            <input
              id="ksp1"
              className="input"
              type="password"
              value={props.pass}
              onChange={(e) => props.setPass(e.target.value)}
              autoComplete="new-password"
              autoFocus
            />
          </div>
          <div className="field" style={{ marginTop: 10 }}>
            <label htmlFor="ksp2">{t("setup.keystore_confirm_passphrase_label")}</label>
            <input
              id="ksp2"
              className="input"
              type="password"
              value={props.pass2}
              onChange={(e) => props.setPass2(e.target.value)}
              autoComplete="new-password"
            />
          </div>
          <Btn
            block
            onClick={props.onExport}
            loading={props.busy}
            disabled={props.busy || props.pass.length < 8 || props.pass !== props.pass2}
            style={{ marginTop: 18 }}
          >
            {t("setup.keystore_download")}
          </Btn>
          <Btn variant="ghost" block onClick={props.onClose} style={{ marginTop: 6 }}>
            {t("setup.cancel")}
          </Btn>
        </div>
      </div>
    </div>
  );
}
